import { type ReactNode } from "react";
import { Button } from "../base/Button";

interface PrintLayoutProps {
  title: ReactNode;
  meta?: ReactNode;
  children: ReactNode;
  /** Optional extra buttons shown on screen but hidden when printing. */
  actions?: ReactNode;
  /** Hide the generic title/M.K. FINANCIAL SERVICES header during printing (defaults to true for standalone forms) */
  hideHeaderOnPrint?: boolean;
}

/**
 * Wraps printable content (receipts, vouchers) in an A4-friendly layout.
 * App chrome (sidebar/top bar) carries the .no-print class, so only this
 * content prints. The actions row is hidden during printing.
 */
export function PrintLayout({ title, meta, children, actions, hideHeaderOnPrint = true }: PrintLayoutProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 print:border-none print:bg-transparent print:p-0 print:shadow-none print:overflow-visible print:h-auto">
      <div className="no-print flex flex-wrap items-center justify-between gap-3 border-b border-background-100 px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground-700">
          <i className="ri-printer-line text-base text-foreground-400" />
          <span>Print preview</span>
        </div>
        <div className="flex items-center gap-2">
          {actions}
          <Button size="sm" variant="outline" icon={<i className="ri-printer-line" />} onClick={() => window.print()}>
            Print
          </Button>
        </div>
      </div>
      <div className="print-area p-6 md:p-8 print:p-0 print:overflow-visible print:h-auto">
        <div className={`mb-4 flex items-start justify-between border-b-2 border-background-200 pb-4 ${hideHeaderOnPrint ? "no-print" : ""}`}>
          <div>
            <h2 className="text-lg font-semibold text-foreground-950">{title}</h2>
            {meta ? <div className="mt-1 text-xs text-foreground-500">{meta}</div> : null}
          </div>
          <div className="text-right text-xs text-foreground-500">
            <p className="text-sm font-semibold text-foreground-800">M.K. FINANCIAL SERVICES</p>
            <p>Chit Fund Management</p>
          </div>
        </div>
        {children}
      </div>
      <style>{`
        @media print {
          .print-area {
            padding: 0 !important;
            overflow: visible !important;
            height: auto !important;
            max-height: none !important;
          }
          table {
            width: 100% !important;
            border-collapse: collapse !important;
            page-break-inside: auto !important;
            break-inside: auto !important;
          }
          thead {
            display: table-header-group !important;
            position: static !important;
          }
          tfoot {
            display: table-footer-group !important;
          }
          tr {
            display: table-row !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          th, td {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>
    </div>
  );
}